import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  BrandOrdererProfile,
  BrandProfileReview,
  getOrdererBrandProfile,
  getOrdererBrandReviews,
  replyToOrdererRating,
} from '../../api/payment/brand-profile.api';
import { BrandProfileMetricsView } from '../../components/brand-deal/brand-profile/brand-profile-metrics';
import BrandReviewList from '../../components/brand-deal/brand-profile/brand-review-list';
import { BRAND_PROFILE_COPY, ordererBadgeLabel } from '../../utils/brand-profile';
import { apiErrorMessage } from '../../utils/api-error-message';

const METRICS_ROLE = 'orderer' as const;

/** Hồ sơ đánh giá của người đặt (spec 2026-10-12 §4) — chỉ người đã đăng nhập xem được (route nằm trong PrivateRoute). */
const BrandOrdererProfilePage: React.FC = () => {
  const { adAccountId = '' } = useParams<{ adAccountId: string }>();
  const [profile, setProfile] = useState<BrandOrdererProfile | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setProfile(null);
    setError(null);
    getOrdererBrandProfile(adAccountId)
      .then((p) => {
        if (alive) setProfile(p);
      })
      .catch((e) => {
        if (alive) setError(apiErrorMessage(e, 'Không tải được hồ sơ người đặt.'));
      });
    return () => {
      alive = false;
    };
  }, [adAccountId]);

  const load = useCallback((page: number) => getOrdererBrandReviews(adAccountId, page), [adAccountId]);
  const canReply = useCallback((r: BrandProfileReview) => r.canReply, []);

  if (error) return <p className="ui-muted p-6 text-center text-[14px]">{error}</p>;
  if (!profile) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="ui-muted animate-spin" />
      </div>
    );
  }

  const badge = ordererBadgeLabel(profile);
  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-3 p-4">
      <div className="ui-card flex flex-col gap-1 p-4">
        <h1 className="ui-heading text-[18px] font-bold">{profile.name}</h1>
        {badge && (
          <span className="w-fit rounded-full bg-emerald-50 px-2 py-0.5 text-[12px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            {badge}
          </span>
        )}
        {profile.isOwner && (
          <span className="ui-muted text-[12px]">Đây là hồ sơ của bạn. Bạn có thể trả lời mỗi đánh giá một lần.</span>
        )}
      </div>
      <BrandProfileMetricsView metrics={profile.metrics} joinedAt={profile.joinedAt} ratingCount={profile.ratingCount} role={METRICS_ROLE} />
      <BrandReviewList
        load={load}
        canReply={canReply}
        sendReply={replyToOrdererRating}
        replyAuthorLabel={BRAND_PROFILE_COPY.ordererReplyLabel}
      />
    </div>
  );
};

export default BrandOrdererProfilePage;
